import React, { useEffect, useState, useMemo } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  ArrowRight,
  FilePdf,
  Package,
  Warning,
  Funnel,
  X,
  Trash,
} from "@phosphor-icons/react";
import { StatusChip, rowTintStyle, STATUS_META } from "@/components/StatusChip";
import { ReceptionChip, findReceptionStatus } from "@/components/ReceptionChip";
import { useReceptionStatuses } from "@/lib/receptionStatuses";
import { DateShortcuts } from "@/components/DateShortcuts";
import { mxDateToUtcISO, todayInMx } from "@/lib/dateShortcuts";
import { usePerms } from "@/lib/perms";

export default function AppointmentsList() {
  const { has } = usePerms();
  const canCreate = has("appointments:create");
  const canDelete = has("appointments:delete");
  const canEdit = has("appointments:status");
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [searchParams] = useSearchParams();
  const { list: receptionList } = useReceptionStatuses();
  const [creators, setCreators] = useState([]);

  useEffect(() => {
    if (searchParams.get("status") || searchParams.get("today")) {
      setShowFilters(true);
    }
  }, [searchParams]);

  const todayIso = () => todayInMx();

  const [filters, setFilters] = useState(() => {
    const initial = {
      provider_id: "",
      unit_type_id: "",
      date_from: "",
      date_to: "",
      has_rejections: "",
      has_faltantes: "",
      status: "",
      reception_status: "",
      created_by: "",
    };
    if (searchParams.get("status")) initial.status = searchParams.get("status");
    if (searchParams.get("reception_status")) initial.reception_status = searchParams.get("reception_status");
    if (searchParams.get("today") === "1") {
      const t = todayIso();
      initial.date_from = t;
      initial.date_to = t;
    }
    return initial;
  });

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.provider_id) params.provider_id = filters.provider_id;
      if (filters.unit_type_id) params.unit_type_id = filters.unit_type_id;
      if (filters.date_from) params.date_from = mxDateToUtcISO(filters.date_from, false);
      if (filters.date_to) params.date_to = mxDateToUtcISO(filters.date_to, true);
      if (filters.has_rejections !== "") params.has_rejections = filters.has_rejections;
      if (filters.has_faltantes !== "") params.has_faltantes = filters.has_faltantes;
      if (filters.status) params.status = filters.status;
      if (filters.reception_status) params.reception_status = filters.reception_status;
      if (filters.created_by) params.created_by = filters.created_by;
      const { data } = await api.get("/appointments", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [p, u, c] = await Promise.all([
          api.get("/catalogs/providers"),
          api.get("/catalogs/unit-types"),
          api.get("/reports/creators"),
        ]);
        setProviders(p.data);
        setUnitTypes(u.data);
        setCreators(c.data || []);
      } catch (err) {
        console.debug("[AppointmentsList] failed to load catalogs", err);
      }
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (a) =>
        a.folio.toLowerCase().includes(s) ||
        (a.provider_name || "").toLowerCase().includes(s) ||
        (a.unit_type_name || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const downloadPdf = async (id, folio, type) => {
    try {
      const token = localStorage.getItem("rcn_token");
      const endpoint = type === "recoleccion" ? `/recolecciones/${id}/pdf` : `/appointments/${id}/pdf`;
      const res = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pase_${folio}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("No se pudo descargar el PDF");
    }
  };

  const removeAppointment = async (a) => {
    if (
      !window.confirm(
        `¿Eliminar la cita ${a.folio}?\n\nEsto borrará también todos sus productos capturados y evidencia. Esta acción no se puede deshacer.`
      )
    )
      return;
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success(`Cita ${a.folio} eliminada`);
      setItems((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clearFilters = () =>
    setFilters({
      provider_id: "",
      unit_type_id: "",
      date_from: "",
      date_to: "",
      has_rejections: "",
      has_faltantes: "",
      status: "",
      reception_status: "",
      created_by: "",
    });

  const changeStatus = async (a, next) => {
    try {
      const { data } = await api.put(`/appointments/${a.id}/status`, { status: next });
      setItems((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...data } : x)));
      toast.success(`${a.folio}: ${STATUS_META[next].label}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeReception = async (a, next) => {
    try {
      const { data } = await api.put(`/appointments/${a.id}/reception-status`, { reception_status: next });
      setItems((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...data } : x)));
      const s = findReceptionStatus(receptionList, next);
      toast.success(`${a.folio}: ${s?.label || next}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
            Historial · Operativo
          </div>
          <h1 className="font-display text-4xl sm:text-5xl">Control de Citas</h1>
        </div>
        {canCreate && (
          <Link
            to="/citas/nueva"
            data-testid="new-appointment-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#0055FF] text-white text-sm font-semibold hover:brightness-110 active:scale-[0.98] transition-transform"
          >
            <Plus size={16} /> Nueva Cita
          </Link>
        )}
      </div>

      <div className="bg-[#111111] border border-white/10 rounded-sm">
        <div className="p-4 border-b border-white/10 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[220px]">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              data-testid="appointments-search"
              placeholder="Buscar por folio, proveedor…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters((v) => !v)}
            data-testid="toggle-filters"
            className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border text-xs font-semibold transition-colors ${
              showFilters || activeFilterCount > 0
                ? "border-[#0055FF] bg-[#0055FF]/10 text-[#0055FF]"
                : "border-white/10 text-white/70 hover:bg-white/5"
            }`}
          >
            <Funnel size={14} />
            Filtros
            {activeFilterCount > 0 && (
              <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded-sm bg-[#0055FF] text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              data-testid="clear-filters"
              className="flex items-center gap-1 text-xs text-white/50 hover:text-white transition-colors"
            >
              <X size={12} /> Limpiar
            </button>
          )}
          <div className="ml-auto text-xs font-mono-data text-white/40">
            {filtered.length} / {items.length} citas
          </div>
        </div>

        {showFilters && (
          <div className="p-4 border-b border-white/10 bg-black/30 space-y-4">
            <div>
              <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
                Rango de fechas
              </label>
              <DateShortcuts
                from={filters.date_from}
                to={filters.date_to}
                onChange={({ from, to }) => setFilters((f) => ({ ...f, date_from: from, date_to: to }))}
                testId="ap-date-shortcuts"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <FilterSelect
                label="Proveedor"
                value={filters.provider_id}
                onChange={(v) => setFilters((f) => ({ ...f, provider_id: v }))}
                options={providers}
                testId="filter-provider"
              />
              <FilterSelect
                label="Unidad"
                value={filters.unit_type_id}
                onChange={(v) => setFilters((f) => ({ ...f, unit_type_id: v }))}
                options={unitTypes}
                testId="filter-unit"
              />
              <div>
                <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
                  Estado
                </label>
                <select
                  data-testid="filter-status"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
                >
                  <option value="">Todos</option>
                  <option value="pending">Pendiente</option>
                  <option value="approved">Aprobada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
                  Recepción
                </label>
                <select
                  data-testid="filter-reception"
                  value={filters.reception_status}
                  onChange={(e) => setFilters((f) => ({ ...f, reception_status: e.target.value }))}
                  className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
                >
                  <option value="">Todas</option>
                  {receptionList.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
                  Rechazos
                </label>
                <select
                  data-testid="filter-rejections"
                  value={filters.has_rejections}
                  onChange={(e) => setFilters((f) => ({ ...f, has_rejections: e.target.value }))}
                  className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
                >
                  <option value="">Todas</option>
                  <option value="true">Con rechazos</option>
                  <option value="false">Sin rechazos</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
                  Faltantes
                </label>
                <select
                  data-testid="filter-faltantes"
                  value={filters.has_faltantes}
                  onChange={(e) => setFilters((f) => ({ ...f, has_faltantes: e.target.value }))}
                  className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
                >
                  <option value="">Todas</option>
                  <option value="true">Con faltantes</option>
                  <option value="false">Sin faltantes</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
                  Creado por
                </label>
                <select
                  data-testid="filter-created-by"
                  value={filters.created_by}
                  onChange={(e) => setFilters((f) => ({ ...f, created_by: e.target.value }))}
                  className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
                >
                  <option value="">Todos</option>
                  {creators.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-white/50 text-sm">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <div className="border border-dashed border-white/10 rounded-sm p-8 max-w-md mx-auto">
              <Package
                size={36}
                className="text-white/20 mx-auto mb-3"
                weight="duotone"
              />
              <div className="text-white/70 font-display text-lg">
                {q || activeFilterCount > 0 ? "Sin resultados" : "Sin citas todavía"}
              </div>
              <div className="text-white/40 text-xs mt-1 mb-4">
                {activeFilterCount > 0
                  ? "Ajusta los filtros o límpialos para ver más citas"
                  : "Crea tu primera cita para comenzar"}
              </div>
              {activeFilterCount === 0 && (
                <Link
                  to="/citas/nueva"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0055FF] text-white text-sm font-semibold"
                >
                  <Plus size={14} /> Nueva Cita
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono-data uppercase tracking-widest text-white/40">
                <tr className="border-b border-white/10">
                  <th className="text-left px-5 py-3">Folio</th>
                  <th className="text-left px-5 py-3">Fecha / Hora</th>
                  <th className="text-left px-5 py-3">Proveedor</th>
                  <th className="text-left px-5 py-3">Unidad</th>
                  <th className="text-right px-5 py-3">U/T/P</th>
                  <th className="text-right px-5 py-3">Productos</th>
                  <th className="text-right px-5 py-3">Rechazos</th>
                  <th className="text-center px-5 py-3">Estado</th>
                  <th className="text-center px-5 py-3">Recepción</th>
                  <th className="text-right px-5 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const dt = safeDate(a.appointment_datetime);
                  return (
                    <tr
                      key={a.id}
                      data-testid="appointment-row"
                      className="border-b border-white/5 hover:brightness-125 transition-all"
                      style={rowTintStyle(a.status)}
                    >
                      <td className="px-5 py-3 font-mono-data font-bold">
                        <div className="flex items-center gap-2">
                          <span className={a.type === "recoleccion" ? "text-[#10B981]" : "text-[#0055FF]"}>
                            {a.folio}
                          </span>
                          {a.type === "recoleccion" && (
                            <span
                              data-testid="row-type-badge"
                              className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-widest border"
                              style={{ borderColor: "#10B981", color: "#10B981", backgroundColor: "rgba(16,185,129,0.08)" }}
                              title="Pase de Recolección"
                            >
                              Recol.
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 font-mono-data text-white/80">{dt}</td>
                      <td className="px-5 py-3">{a.provider_name}</td>
                      <td className="px-5 py-3 text-white/70">{a.unit_type_name}</td>
                      <td className="px-5 py-3 text-right font-mono-data text-white/80">
                        {a.type === "recoleccion"
                          ? <span className="text-white/30">—</span>
                          : <>{a.qty_units}/{a.qty_pallets}/{a.qty_pieces}</>}
                      </td>
                      <td className="px-5 py-3 text-right font-mono-data">
                        {a.products_count}
                      </td>
                      <td className="px-5 py-3 text-right font-mono-data">
                        {a.rejected_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[#FF3B30]">
                            <Warning size={12} weight="fill" /> {a.rejected_count}
                          </span>
                        ) : (
                          <span className="text-white/40">0</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <StatusChip
                          status={a.status}
                          testId="row-status-chip"
                          disabled={!canEdit}
                          onChange={(next) => changeStatus(a, next)}
                        />
                      </td>
                      <td className="px-5 py-3 text-center">
                        <ReceptionChip
                          status={a.reception_status}
                          testId="row-reception-chip"
                          disabled={!canEdit}
                          onChange={(next) => changeReception(a, next)}
                        />
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            title="Descargar Pase PDF"
                            data-testid="row-pdf-btn"
                            onClick={() => downloadPdf(a.id, a.folio, a.type)}
                            className="p-1.5 rounded-sm border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-colors"
                          >
                            <FilePdf size={14} />
                          </button>
                          <Link
                            to={a.type === "recoleccion" ? `/recolecciones/${a.id}` : `/citas/${a.id}`}
                            data-testid="row-open-btn"
                            className="p-1.5 rounded-sm border border-white/10 text-white/60 hover:text-white hover:border-[#0055FF] hover:bg-[#0055FF]/10 transition-colors"
                          >
                            <ArrowRight size={14} />
                          </Link>
                          {canDelete && (
                            <button
                              title="Eliminar cita"
                              data-testid="row-delete-btn"
                              onClick={() => removeAppointment(a)}
                              className="p-1.5 rounded-sm border border-white/10 text-white/60 hover:text-[#FF3B30] hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 transition-colors"
                            >
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
        {label}
      </label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
      >
        <option value="">Todos</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function safeDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Mexico_City",
    });
  } catch (_e) {
    return iso;
  }
}
